/**
 * Normalizes a path so comparisons are not affected by casing, query strings,
 * hashes or missing/extra slashes.
 *
 * e.g. `Mottoaligners?src=ops` -> `/mottoaligners/`
 */
function normalizePath(value: string): string {
	const [pathOnly = ''] = value.split(/[?#]/);
	const trimmed = pathOnly.trim().toLowerCase();

	if (!trimmed) {
		return '/';
	}

	const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

	return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
}

/**
 * Checks whether a pathname matches any of the given path patterns.
 *
 * Two pattern formats are supported:
 * - exact: `/dts/` matches only `/dts/`
 * - wildcard: `/mottoaligners/*` matches `/mottoaligners/` and every page below it
 *
 * Matching ignores casing, trailing slashes, query strings and hashes.
 *
 * @param pathname - Current pathname, e.g. from `usePathname()`
 * @param patterns - List of path patterns, typically configured in Contentful
 * @returns true when the pathname matches at least one pattern
 */
export function isPathMatched(
	pathname: string | null | undefined,
	patterns: Array<string | null | undefined> | null | undefined
): boolean {
	if (!pathname?.trim() || !patterns?.length) {
		return false;
	}

	const normalizedPathname = normalizePath(pathname);

	return patterns.some((pattern) => {
		const trimmedPattern = pattern?.trim();

		if (!trimmedPattern) {
			return false;
		}

		if (trimmedPattern.endsWith('*')) {
			// `/mottoaligners/*` -> `/mottoaligners/`, which also matches the base path itself
			return normalizedPathname.startsWith(normalizePath(trimmedPattern.slice(0, -1)));
		}

		return normalizedPathname === normalizePath(trimmedPattern);
	});
}
